"use client";

import { useEffect, useRef, useState } from "react";
import { useTheme } from "next-themes";
import { useTranslations } from "next-intl";
import { GoogleIcon } from "@/components/icons/google-icon";
import { cn } from "@/lib/utils";
import {
  setGoogleCallback,
  renderGoogleButton,
  isGoogleReady,
  getGoogleSignInAvailability,
  type GoogleSignInUnavailableReason,
} from "@/lib/google-signin";

interface GoogleSignInButtonProps {
  onCredential: (credential: string) => void;
  className?: string;
}

function getUnavailableReasonKey(reason: GoogleSignInUnavailableReason): string {
  switch (reason) {
    case "missing_client_id":
      return "googleUnavailableMissingConfig";
    case "native_context":
      return "googleUnavailableNative";
    case "https_required":
      return "googleUnavailableHttpsRequired";
    case "ip_origin_not_supported":
      return "googleUnavailableIpOrigin";
    case "origin_not_allowed":
      return "googleUnavailableOriginNotAllowed";
  }
}

/**
 * Custom-styled Google sign-in card with an invisible GIS iframe overlay.
 *
 * The user sees our design-system card (icon + label + hint). The actual
 * click is captured by the invisible Google iframe sitting on top, which
 * reliably opens the Google sign-in popup and returns the JWT ID token.
 */
export function GoogleSignInButton({
  onCredential,
  className,
}: GoogleSignInButtonProps) {
  const t = useTranslations("auth");
  const iframeRef = useRef<HTMLDivElement>(null);
  const callbackRef = useRef(onCredential);
  const [availability, setAvailability] = useState<ReturnType<
    typeof getGoogleSignInAvailability
  > | null>(null);
  const [ready, setReady] = useState(false);
  const { resolvedTheme } = useTheme();
  const colorScheme = resolvedTheme === "dark" ? "dark" : "light";
  const enabled = availability?.enabled ?? true;
  const unavailableReason = availability && !availability.enabled ? availability.reason : null;
  const unavailableMessage = unavailableReason
    ? t(getUnavailableReasonKey(unavailableReason))
    : t("googleHint");

  useEffect(() => {
    callbackRef.current = onCredential;
  }, [onCredential]);

  // Evaluate once on mount in the browser to avoid SSR/client mismatch.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setAvailability(getGoogleSignInAvailability());
  }, []);

  // Poll for GIS script only when Google sign-in is enabled for this origin.
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => {
      if (isGoogleReady()) {
        clearInterval(id);
        setReady(true);
      }
    }, 100);
    return () => clearInterval(id);
  }, [enabled]);

  // Render the invisible Google button + re-render on theme change
  useEffect(() => {
    if (!enabled || !ready || !iframeRef.current) return;
    setGoogleCallback((credential) => callbackRef.current(credential));
    renderGoogleButton(iframeRef.current, colorScheme);
  }, [enabled, ready, colorScheme]);

  useEffect(() => {
    if (!enabled) return;
    setGoogleCallback((credential) => callbackRef.current(credential));
  }, [enabled]);

  return (
    <div className={cn("relative", enabled && "group", className)} aria-disabled={!enabled}>
      {/* Our styled card (visible) — uses group-hover since the overlay absorbs direct hover */}
      <div
        className={cn(
          "auth-method-option auth-method-option--group flex w-full min-h-[5.5rem] items-center gap-4 rounded-xl border border-border bg-card p-4 text-left transition-colors",
          enabled
            ? ""
            : "cursor-not-allowed opacity-70",
        )}
      >
        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
          <GoogleIcon className="size-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-base font-semibold leading-tight">
            {t("continueWithGoogle")}
          </span>
          <span className="block text-sm text-muted-foreground">
            {unavailableMessage}
          </span>
        </span>
      </div>

      {/* Invisible Google iframe overlay.
          The GIS iframe is ~44px tall; our card is ~88px.
          scaleY(3) on the wrapper stretches the hit-area to cover
          the full card height. opacity-[0.01] hides the visual. */}
      {enabled && (
        <div
          ref={iframeRef}
          className="absolute inset-0 z-10 flex cursor-pointer items-center justify-center overflow-hidden opacity-[0.01] [&>div]:scale-y-[3] [&>div]:origin-center"
        />
      )}
    </div>
  );
}
